import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";
import {
  ArrowsRightLeftIcon,
  BuildingOffice2Icon,
  MapPinIcon,
  TruckIcon,
  MinusIcon,
  PlusIcon,
  ClockIcon,
  XMarkIcon,
  CheckCircleIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { StarIcon as StarSolid } from "@heroicons/react/24/solid";
import { TODAY, TRIPS, CITIES, CLS_STYLE, TripItem, addDays, fDateCap, money, norm, remainingSeats } from "./reservation-types";
import { ReservationWizardModal, WizState, newWiz, applyWizard } from "./reservation-modals";
import type { ReservationsStore } from "./reservations-console";

// Booking search engine: bus trips (main service), hotels and taxis.
// Bus bookings go through the reservation wizard and land in the shared reservations list;
// hotel and taxi are pre-bookings (demo) until those services are wired.

type Service = "bus" | "hotel" | "taxi";
const AGENT = "yasmine";
const RECENT_KEY = "reservations.recentSearches";

const WEEKDAY = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const dayLabel = (iso: string) => {
  const d = new Date(`${iso}T12:00:00`);
  return { wd: WEEKDAY[d.getDay()], day: d.getDate() };
};
const hourOf = (h: string) => parseInt(h.slice(0, 2), 10);
const PERIODS = [
  { k: "all", label: "Toute la journée", test: () => true },
  { k: "morning", label: "Matin", test: (h: number) => h < 12 },
  { k: "afternoon", label: "Après-midi", test: (h: number) => h >= 12 && h < 17 },
  { k: "evening", label: "Soir", test: (h: number) => h >= 17 },
] as const;

/* -------------------------------- Demo data ------------------------------- */

type Hotel = { id: string; name: string; city: string; quartier: string; stars: number; rating: number; reviews: number; price: number; roomsLeft: number; amenities: string[] };
const HOTELS: Hotel[] = [
  { id: "h1", name: "Hôtel Akwa Palace", city: "DOUALA", quartier: "Akwa", stars: 4, rating: 8.6, reviews: 412, price: 65000, roomsLeft: 4, amenities: ["Wi-Fi", "Petit-déjeuner", "Piscine", "Parking"] },
  { id: "h2", name: "Résidence Bonapriso", city: "DOUALA", quartier: "Bonapriso", stars: 3, rating: 8.1, reviews: 188, price: 38000, roomsLeft: 9, amenities: ["Wi-Fi", "Parking", "Navette aéroport"] },
  { id: "h3", name: "Ibis Douala", city: "DOUALA", quartier: "Bonanjo", stars: 3, rating: 7.8, reviews: 903, price: 47000, roomsLeft: 2, amenities: ["Wi-Fi", "Petit-déjeuner"] },
  { id: "h4", name: "Auberge du Wouri", city: "DOUALA", quartier: "Bonabéri", stars: 2, rating: 7.2, reviews: 64, price: 18000, roomsLeft: 6, amenities: ["Wi-Fi", "Parking"] },
  { id: "h5", name: "Hilton Yaoundé", city: "YAOUNDÉ", quartier: "Centre administratif", stars: 5, rating: 8.9, reviews: 1204, price: 115000, roomsLeft: 7, amenities: ["Wi-Fi", "Petit-déjeuner", "Piscine", "Parking", "Navette aéroport"] },
  { id: "h6", name: "Hôtel Djeuga Palace", city: "YAOUNDÉ", quartier: "Centre-ville", stars: 4, rating: 8.0, reviews: 356, price: 58000, roomsLeft: 3, amenities: ["Wi-Fi", "Piscine", "Parking"] },
  { id: "h7", name: "Résidence Bastos", city: "YAOUNDÉ", quartier: "Bastos", stars: 3, rating: 8.4, reviews: 97, price: 35000, roomsLeft: 5, amenities: ["Wi-Fi", "Petit-déjeuner", "Parking"] },
  { id: "h8", name: "Ilomba Hôtel", city: "KRIBI", quartier: "Mpalla", stars: 4, rating: 9.1, reviews: 281, price: 72000, roomsLeft: 1, amenities: ["Wi-Fi", "Petit-déjeuner", "Piscine", "Plage"] },
  { id: "h9", name: "Kribi Beach Lodge", city: "KRIBI", quartier: "Ngoyè", stars: 3, rating: 8.3, reviews: 143, price: 41000, roomsLeft: 8, amenities: ["Wi-Fi", "Plage", "Parking"] },
  { id: "h10", name: "Talotel Bafoussam", city: "BAFOUSSAM", quartier: "Tamdja", stars: 3, rating: 7.9, reviews: 122, price: 32000, roomsLeft: 10, amenities: ["Wi-Fi", "Petit-déjeuner", "Parking"] },
];
const HOTEL_CITIES = Array.from(new Set(HOTELS.map((h) => h.city)));
const AMENITIES = ["Wi-Fi", "Petit-déjeuner", "Piscine", "Parking", "Navette aéroport", "Plage"];

const LANDMARKS: Record<string, string[]> = {
  DOUALA: ["Aéroport international de Douala", "Gare routière Akwa", "Carrefour Ndokoti", "Rond-point Deïdo", "Bonamoussadi Carrefour", "Marché Sandaga", "Bonapriso Super U"],
  YAOUNDÉ: ["Aéroport de Nsimalen", "Gare de Mvan", "Poste centrale", "Carrefour Bastos", "Omnisport", "Mvog-Mbi"],
  KRIBI: ["Centre-ville Kribi", "Plage de Ngoyè", "Chutes de la Lobé", "Port de Kribi"],
  BAFOUSSAM: ["Marché A", "Tamdja", "Gare routière Bafoussam"],
};
const TAXI_CLASSES = [
  { k: "moto", label: "Moto-taxi", seats: 1, base: 300, perKm: 100, speed: 28, note: "Rapide dans les embouteillages" },
  { k: "standard", label: "Taxi standard", seats: 4, base: 700, perKm: 250, speed: 22, note: "Taxi jaune, course privée" },
  { k: "confort", label: "Confort climatisé", seats: 4, base: 1500, perKm: 400, speed: 22, note: "Berline récente, climatisation" },
  { k: "van", label: "Van", seats: 7, base: 3000, perKm: 550, speed: 20, note: "Familles et bagages" },
];

// Stable pseudo-distance (km) between two places.
const distanceKm = (a: string, b: string) => {
  let h = 7;
  for (const c of `${a}|${b}`) h = (h * 31 + c.charCodeAt(0)) % 9973;
  return 3 + (h % 22);
};

/* ---------------------------------- Page ---------------------------------- */

export default function RechercheReservation({ store }: { store: ReservationsStore }) {
  const [service, setService] = useState<Service>("bus");
  const [toast, setToast] = useState<string | null>(null);
  const flash = (m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 2600);
  };

  const tabs: { k: Service; label: string; sub: string; icon: React.ComponentType<{ className?: string }>; badge?: string }[] = [
    { k: "bus", label: "Bus", sub: "Billets interurbains", icon: TruckIcon, badge: "Principal" },
    { k: "hotel", label: "Hôtel", sub: "Chambres & séjours", icon: BuildingOffice2Icon },
    { k: "taxi", label: "Taxi", sub: "Courses en ville", icon: MapPinIcon },
  ];

  return (
    <div className="p-4 md:p-6 lg:p-7 text-[#1f1b2e] font-['Public_Sans',sans-serif]">
      <div className="rounded-2xl bg-gradient-to-br from-[#2e1065] via-[#4c1d95] to-[#6d28d9] p-5 md:p-6 text-white shadow-lg">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Moteur de recherche</h1>
            <p className="text-sm text-purple-100 mt-1">Trouvez un départ, une chambre ou une course en quelques secondes.</p>
          </div>
          <div className="flex p-1 bg-white/10 rounded-xl gap-1">
            {tabs.map((t) => (
              <button
                key={t.k}
                type="button"
                onClick={() => setService(t.k)}
                className={classNames("flex items-center gap-2 px-3.5 py-2 rounded-lg text-left transition-all", service === t.k ? "bg-white text-[#4c1d95] shadow" : "text-purple-100 hover:bg-white/10")}
              >
                <t.icon className="w-5 h-5 shrink-0" />
                <span>
                  <span className="flex items-center gap-1.5 text-sm font-bold">
                    {t.label}
                    {t.badge && <span className={classNames("px-1.5 rounded text-[9px] font-bold uppercase", service === t.k ? "bg-[#ede9fe] text-[#6d28d9]" : "bg-white/20")}>{t.badge}</span>}
                  </span>
                  <span className={classNames("block text-[10.5px]", service === t.k ? "text-[#7c729b]" : "text-purple-200")}>{t.sub}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
        {service === "bus" && <BusSearch store={store} flash={flash} />}
        {service === "hotel" && <HotelSearch flash={flash} />}
        {service === "taxi" && <TaxiSearch flash={flash} />}
      </div>
      {toast && <div className="fixed left-1/2 bottom-6 -translate-x-1/2 z-[95] px-4 py-2.5 bg-[#1f1b2e] rounded-full text-xs font-semibold text-white shadow-xl">{toast}</div>}
    </div>
  );
}

/* ----------------------------------- Bus ---------------------------------- */

function BusSearch({ store, flash }: { store: ReservationsStore; flash: (m: string) => void }) {
  const [from, setFrom] = useState("DOUALA");
  const [to, setTo] = useState("YAOUNDÉ");
  const [date, setDate] = useState(TODAY);
  const [pax, setPax] = useState(1);
  const [cls, setCls] = useState("TOUS");
  const [period, setPeriod] = useState<(typeof PERIODS)[number]["k"]>("all");
  const [sort, setSort] = useState<"heure" | "prix" | "places">("heure");
  const [wiz, setWiz] = useState<WizState | null>(null);
  const [recent, setRecent] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    } catch {
      return [];
    }
  });

  // Remember the route searched (debounced by route change).
  useEffect(() => {
    if (!from || !to || from === to) return;
    const key = `${from}|${to}`;
    setRecent((prev) => {
      const next = [key, ...prev.filter((k) => k !== key)].slice(0, 4);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  }, [from, to]);

  const routeTrips = TRIPS.filter((t) => (from === "*" || t.from === from) && (to === "*" || t.to === to) && (cls === "TOUS" || t.cls === cls));
  const dayTrips = routeTrips
    .filter((t) => t.date === date && PERIODS.find((p) => p.k === period)!.test(hourOf(t.heure)))
    .map((t) => ({ t, rem: remainingSeats(t, store.list) }))
    .sort((a, b) => (sort === "prix" ? a.t.prix - b.t.prix : sort === "places" ? b.rem - a.rem : a.t.heure.localeCompare(b.t.heure)));
  const strip = Array.from({ length: 7 }, (_, i) => addDays(TODAY, i)).map((d) => {
    const ts = routeTrips.filter((t) => t.date === d);
    return { d, count: ts.length, min: ts.length ? Math.min(...ts.map((t) => t.prix)) : 0 };
  });
  const cheapest = dayTrips.length ? Math.min(...dayTrips.map((x) => x.t.prix)) : 0;
  const otherDates = Array.from(new Set(routeTrips.filter((t) => t.date >= TODAY && t.date !== date).map((t) => t.date))).sort().slice(0, 3);
  const popular = useMemo(() => {
    const counts: Record<string, number> = {};
    TRIPS.forEach((t) => (counts[`${t.from}|${t.to}`] = (counts[`${t.from}|${t.to}`] || 0) + 1));
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([k]) => k);
  }, []);

  const book = (t: TripItem) => {
    const w = newWiz("resa");
    setWiz({ ...w, trip: t.id, step: 2, adv: pax > 1, proches: Array.from({ length: pax - 1 }, () => ({ name: "", phone: "" })) });
  };
  const bookOpen = () => setWiz({ ...newWiz("resa"), trip: "OPEN", step: 2, oFrom: from === "*" ? "DOUALA" : from, oTo: to === "*" ? "YAOUNDÉ" : to, oDate: date });

  const selectCls = "w-full h-11 px-3 rounded-xl bg-white text-[#1f1b2e] text-sm font-semibold border-0 focus:outline-none focus:ring-2 focus:ring-purple-300";
  const label = "block text-[10.5px] font-bold uppercase tracking-wider text-purple-200 mb-1";

  return (
    <>
      {/* Form */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_170px_150px] gap-3 items-end mt-5">
        <label className="block">
          <span className={label}>Départ</span>
          <select value={from} onChange={(e) => setFrom(e.target.value)} className={selectCls}>
            <option value="*">Toutes les villes</option>
            {CITIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => {
            setFrom(to);
            setTo(from);
          }}
          title="Inverser départ et arrivée"
          className="h-11 w-11 mx-auto rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
        >
          <ArrowsRightLeftIcon className="w-5 h-5" />
        </button>
        <label className="block">
          <span className={label}>Arrivée</span>
          <select value={to} onChange={(e) => setTo(e.target.value)} className={selectCls}>
            <option value="*">Toutes destinations</option>
            {CITIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className={label}>Date</span>
          <input type="date" value={date} min={TODAY} onChange={(e) => setDate(e.target.value || TODAY)} className={selectCls} />
        </label>
        <div>
          <span className={label}>Passagers</span>
          <Stepper value={pax} min={1} max={9} onChange={setPax} />
        </div>
      </div>

      {/* Quick routes */}
      <div className="flex items-center gap-2 flex-wrap mt-4 text-xs">
        <span className="text-purple-200 font-semibold">{recent.length ? "Récentes :" : "Populaires :"}</span>
        {(recent.length ? recent : popular).map((k) => {
          const [a, b] = k.split("|");
          return (
            <button
              key={k}
              type="button"
              onClick={() => {
                setFrom(a);
                setTo(b);
              }}
              className={classNames("px-2.5 py-1 rounded-full font-semibold", from === a && to === b ? "bg-white text-[#4c1d95]" : "bg-white/10 hover:bg-white/20")}
            >
              {a} → {b}
            </button>
          );
        })}
      </div>

      {/* Date strip */}
      <div className="grid grid-cols-7 gap-1.5 mt-4">
        {strip.map((s) => (
          <button
            key={s.d}
            type="button"
            onClick={() => setDate(s.d)}
            className={classNames("rounded-xl py-2 text-center transition-all", date === s.d ? "bg-white text-[#4c1d95] shadow" : "bg-white/10 hover:bg-white/20")}
          >
            <div className={classNames("text-[10px] font-bold uppercase", date === s.d ? "text-[#7c729b]" : "text-purple-200")}>
              {s.d === TODAY ? "Auj." : dayLabel(s.d).wd}
            </div>
            <div className="text-base font-bold leading-tight">{dayLabel(s.d).day}</div>
            <div className={classNames("text-[10px] font-semibold", date === s.d ? "text-[#6d28d9]" : "text-purple-100")}>{s.count ? `dès ${s.min.toLocaleString("fr-FR")}` : "—"}</div>
          </button>
        ))}
      </div>

      {/* Results */}
      <div className="mt-5 -mx-5 md:-mx-6 -mb-5 md:-mb-6 p-4 md:p-5 bg-[#faf7ff] rounded-b-2xl text-[#1f1b2e]">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <div className="text-sm font-bold">
            {dayTrips.length} départ{dayTrips.length > 1 ? "s" : ""} · {fDateCap(date)}
          </div>
          <div className="flex p-0.5 bg-[#ede9fe] rounded-lg gap-0.5 ml-auto flex-wrap">
            {PERIODS.map((p) => (
              <button key={p.k} type="button" onClick={() => setPeriod(p.k)} className={classNames("px-2.5 py-1 rounded-md text-xs font-semibold", period === p.k ? "bg-white text-[#4c1d95] shadow-xs" : "text-[#7c729b]")}>
                {p.label}
              </button>
            ))}
          </div>
          <select value={cls} onChange={(e) => setCls(e.target.value)} className="h-8 px-2 rounded-lg border border-[#e2e8f0] bg-white text-xs">
            <option value="TOUS">Toutes classes</option>
            <option value="CLASSIQUE">Classique</option>
            <option value="VIP">VIP</option>
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value as typeof sort)} className="h-8 px-2 rounded-lg border border-[#e2e8f0] bg-white text-xs">
            <option value="heure">Trier : heure de départ</option>
            <option value="prix">Trier : prix</option>
            <option value="places">Trier : places disponibles</option>
          </select>
        </div>

        <div className="grid gap-2.5">
          {dayTrips.map(({ t, rem }) => {
            const enough = rem >= pax;
            const c = CLS_STYLE[t.cls];
            const pct = Math.round(((t.cap - rem) / t.cap) * 100);
            return (
              <div key={t.id} className="p-4 bg-white border border-[#ece7f8] rounded-xl flex items-center gap-4 flex-wrap hover:border-purple-200">
                <div className="w-16 shrink-0">
                  <div className="text-xl font-bold leading-none">{t.heure}</div>
                  <div className="text-[11px] text-gray-500 mt-1">{t.date === TODAY ? "aujourd'hui" : dayLabel(t.date).wd}</div>
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 font-bold text-sm">
                    {t.from} <span className="text-gray-300">→</span> {t.to}
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" style={{ background: c.bg, color: c.fg }}>
                      {t.cls}
                    </span>
                    {t.prix === cheapest && dayTrips.length > 1 && <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-50 text-emerald-700">Meilleur prix</span>}
                  </div>
                  <div className="text-xs text-gray-500 mt-1 font-mono">{t.bus}</div>
                </div>
                <div className="w-36">
                  <div className={classNames("text-xs font-bold", rem === 0 ? "text-rose-600" : rem < 10 ? "text-amber-700" : "text-emerald-700")}>{rem === 0 ? "Complet" : `${rem} place${rem > 1 ? "s" : ""} libre${rem > 1 ? "s" : ""}`}</div>
                  <div className="h-1.5 mt-1 bg-gray-100 rounded-full overflow-hidden" title={`${pct} % rempli`}>
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: pct > 85 ? "#ef4444" : pct > 60 ? "#f59e0b" : "#22c55e" }} />
                  </div>
                </div>
                <div className="text-right w-32">
                  <div className="text-lg font-bold">{money(t.prix * pax)}</div>
                  <div className="text-[11px] text-gray-500">{pax > 1 ? `${pax} × ${money(t.prix)}` : "par passager"}</div>
                </div>
                <button
                  type="button"
                  disabled={!enough}
                  onClick={() => book(t)}
                  className="h-10 px-5 rounded-lg bg-[#6d28d9] hover:bg-purple-800 disabled:bg-gray-200 disabled:text-gray-400 text-white text-sm font-bold"
                  title={enough ? "Réserver ce départ" : `Seulement ${rem} place(s) pour ${pax} passager(s)`}
                >
                  {enough ? "Réserver" : rem === 0 ? "Complet" : "Places insuffisantes"}
                </button>
              </div>
            );
          })}
          {dayTrips.length === 0 && (
            <div className="p-6 bg-white border border-dashed border-[#ddd6fe] rounded-xl text-center">
              <div className="text-sm font-bold">Aucun départ pour ces critères.</div>
              {otherDates.length > 0 ? (
                <div className="mt-3 flex items-center justify-center gap-2 flex-wrap text-xs">
                  <SparklesIcon className="w-4 h-4 text-[#6d28d9]" />
                  <span className="text-gray-600">Prochains départs sur ce trajet :</span>
                  {otherDates.map((d) => (
                    <button key={d} type="button" onClick={() => setDate(d)} className="px-2.5 py-1 rounded-full bg-[#ede9fe] text-[#5b21b6] font-semibold">
                      {fDateCap(d)}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-gray-500 mt-1">Aucun départ programmé sur ce trajet dans les jours à venir.</div>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap mt-3 text-xs text-gray-500">
          <span>Prix TTC par passager · places mises à jour en temps réel avec les réservations de la console.</span>
          <button type="button" onClick={bookOpen} className="h-9 px-3.5 border border-[#d4d4d8] bg-white rounded-lg font-semibold text-[#241f36] hover:bg-gray-50">
            Réserver sur voyage indéterminé
          </button>
        </div>
      </div>

      {wiz && (
        <ReservationWizardModal
          wiz={wiz}
          setWiz={setWiz}
          list={store.list}
          onClose={() => setWiz(null)}
          onCommit={(w) => {
            const res = applyWizard(store.list, store.seq, w, AGENT);
            store.setList(res.list);
            store.setSeq(res.seq);
            flash("Réservation enregistrée — visible dans le menu Réservations");
            return res.ids;
          }}
          onShowTicket={() => flash("Ouvrez la réservation depuis le menu Réservations pour imprimer le ticket")}
          onFlash={flash}
        />
      )}
    </>
  );
}

/* ---------------------------------- Hotel --------------------------------- */

function HotelSearch({ flash }: { flash: (m: string) => void }) {
  const [city, setCity] = useState("DOUALA");
  const [checkIn, setCheckIn] = useState(TODAY);
  const [checkOut, setCheckOut] = useState(addDays(TODAY, 2));
  const [guests, setGuests] = useState(2);
  const [rooms, setRooms] = useState(1);
  const [maxPrice, setMaxPrice] = useState(120000);
  const [minStars, setMinStars] = useState(0);
  const [amen, setAmen] = useState<string[]>([]);
  const [sort, setSort] = useState<"prix" | "note">("note");
  const [booking, setBooking] = useState<Hotel | null>(null);

  const nights = Math.max(1, Math.round((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 86400000));
  const results = HOTELS.filter((h) => h.city === city && h.price <= maxPrice && h.stars >= minStars && amen.every((a) => h.amenities.includes(a)) && h.roomsLeft >= rooms).sort((a, b) =>
    sort === "prix" ? a.price - b.price : b.rating - a.rating
  );
  const selectCls = "w-full h-11 px-3 rounded-xl bg-white text-[#1f1b2e] text-sm font-semibold border-0 focus:outline-none focus:ring-2 focus:ring-purple-300";
  const label = "block text-[10.5px] font-bold uppercase tracking-wider text-purple-200 mb-1";

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-[1.3fr_1fr_1fr_140px_140px] gap-3 items-end mt-5">
        <label className="block col-span-2 md:col-span-1">
          <span className={label}>Ville</span>
          <select value={city} onChange={(e) => setCity(e.target.value)} className={selectCls}>
            {HOTEL_CITIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className={label}>Arrivée</span>
          <input
            type="date"
            value={checkIn}
            min={TODAY}
            onChange={(e) => {
              const v = e.target.value || TODAY;
              setCheckIn(v);
              if (checkOut <= v) setCheckOut(addDays(v, 1));
            }}
            className={selectCls}
          />
        </label>
        <label className="block">
          <span className={label}>Départ</span>
          <input type="date" value={checkOut} min={addDays(checkIn, 1)} onChange={(e) => setCheckOut(e.target.value || addDays(checkIn, 1))} className={selectCls} />
        </label>
        <div>
          <span className={label}>Voyageurs</span>
          <Stepper value={guests} min={1} max={12} onChange={setGuests} />
        </div>
        <div>
          <span className={label}>Chambres</span>
          <Stepper value={rooms} min={1} max={6} onChange={setRooms} />
        </div>
      </div>

      <div className="mt-5 -mx-5 md:-mx-6 -mb-5 md:-mb-6 p-4 md:p-5 bg-[#faf7ff] rounded-b-2xl text-[#1f1b2e] grid md:grid-cols-[220px_1fr] gap-4">
        <aside className="space-y-4 text-xs">
          <div>
            <div className="font-bold mb-1.5">Prix max / nuit</div>
            <input type="range" min={15000} max={120000} step={5000} value={maxPrice} onChange={(e) => setMaxPrice(Number(e.target.value))} className="w-full accent-[#6d28d9]" />
            <div className="text-gray-600">{money(maxPrice)}</div>
          </div>
          <div>
            <div className="font-bold mb-1.5">Catégorie minimum</div>
            <div className="flex gap-1">
              {[0, 2, 3, 4, 5].map((s) => (
                <button key={s} type="button" onClick={() => setMinStars(s)} className={classNames("px-2 h-8 rounded-lg border font-semibold", minStars === s ? "border-[#6d28d9] bg-[#f5f1ff] text-[#4c1d95]" : "border-[#e2e8f0] bg-white")}>
                  {s === 0 ? "Tous" : `${s}★`}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="font-bold mb-1.5">Équipements</div>
            <div className="flex flex-wrap gap-1.5">
              {AMENITIES.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAmen((x) => (x.includes(a) ? x.filter((y) => y !== a) : [...x, a]))}
                  className={classNames("px-2.5 py-1 rounded-full border font-semibold", amen.includes(a) ? "border-[#6d28d9] bg-[#f5f1ff] text-[#4c1d95]" : "border-[#e2e8f0] bg-white text-gray-600")}
                >
                  {a}
                </button>
              ))}
            </div>
          </div>
        </aside>
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="text-sm font-bold">
              {results.length} hébergement{results.length > 1 ? "s" : ""} à {city} · {nights} nuit{nights > 1 ? "s" : ""} · {guests} voyageur{guests > 1 ? "s" : ""}
            </div>
            <select value={sort} onChange={(e) => setSort(e.target.value as typeof sort)} className="ml-auto h-8 px-2 rounded-lg border border-[#e2e8f0] bg-white text-xs">
              <option value="note">Trier : mieux notés</option>
              <option value="prix">Trier : prix</option>
            </select>
          </div>
          <div className="grid gap-2.5">
            {results.map((h) => (
              <div key={h.id} className="p-4 bg-white border border-[#ece7f8] rounded-xl flex gap-4 flex-wrap items-center hover:border-purple-200">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#ede9fe] to-[#ddd6fe] flex items-center justify-center text-[#5b21b6] font-bold shrink-0">
                  <BuildingOffice2Icon className="w-7 h-7" />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-sm">{h.name}</span>
                    <span className="flex">
                      {Array.from({ length: h.stars }).map((_, i) => (
                        <StarSolid key={i} className="w-3 h-3 text-amber-400" />
                      ))}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {h.quartier} · {h.amenities.join(" · ")}
                  </div>
                  {h.roomsLeft <= 3 && <div className="text-[11px] font-semibold text-rose-600 mt-1">Plus que {h.roomsLeft} chambre{h.roomsLeft > 1 ? "s" : ""}</div>}
                </div>
                <div className="text-center w-16">
                  <div className="inline-flex px-2 py-1 rounded-lg bg-[#4c1d95] text-white text-sm font-bold">{h.rating.toFixed(1)}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{h.reviews} avis</div>
                </div>
                <div className="text-right w-36">
                  <div className="text-lg font-bold">{money(h.price * nights * rooms)}</div>
                  <div className="text-[11px] text-gray-500">
                    {money(h.price)} × {nights} nuit{nights > 1 ? "s" : ""}
                    {rooms > 1 ? ` × ${rooms} ch.` : ""}
                  </div>
                </div>
                <button type="button" onClick={() => setBooking(h)} className="h-10 px-5 rounded-lg bg-[#6d28d9] hover:bg-purple-800 text-white text-sm font-bold">
                  Pré-réserver
                </button>
              </div>
            ))}
            {results.length === 0 && (
              <div className="p-6 bg-white border border-dashed border-[#ddd6fe] rounded-xl text-center text-sm">
                Aucun hébergement ne correspond. <button type="button" onClick={() => { setMaxPrice(120000); setMinStars(0); setAmen([]); }} className="text-[#6d28d9] font-semibold">Élargir les filtres</button>
              </div>
            )}
          </div>
        </div>
      </div>

      {booking && (
        <PreBookingModal
          title={booking.name}
          lines={[`${city} · ${booking.quartier}`, `${fDateCap(checkIn)} → ${fDateCap(checkOut)} (${nights} nuit${nights > 1 ? "s" : ""})`, `${rooms} chambre${rooms > 1 ? "s" : ""} · ${guests} voyageur${guests > 1 ? "s" : ""}`]}
          total={booking.price * nights * rooms}
          prefix="HTL"
          onClose={() => setBooking(null)}
          onDone={(code) => flash(`Pré-réservation hôtel ${code} envoyée à l'établissement`)}
        />
      )}
    </>
  );
}

/* ---------------------------------- Taxi ---------------------------------- */

function TaxiSearch({ flash }: { flash: (m: string) => void }) {
  const [city, setCity] = useState("DOUALA");
  const [pickup, setPickup] = useState("Aéroport international de Douala");
  const [dest, setDest] = useState("Bonapriso Super U");
  const [when, setWhen] = useState<"now" | "later">("now");
  const [at, setAt] = useState(`${TODAY}T18:00`);
  const [pax, setPax] = useState(1);
  const [booking, setBooking] = useState<(typeof TAXI_CLASSES)[number] | null>(null);

  const places = LANDMARKS[city] ?? [];
  const valid = pickup.trim() && dest.trim() && norm(pickup) !== norm(dest);
  const km = valid ? distanceKm(norm(pickup), norm(dest)) : 0;
  const hour = when === "now" ? new Date().getHours() : parseInt(at.slice(11, 13), 10);
  const night = hour >= 22 || hour < 6;
  const selectCls = "w-full h-11 px-3 rounded-xl bg-white text-[#1f1b2e] text-sm font-semibold border-0 focus:outline-none focus:ring-2 focus:ring-purple-300";
  const label = "block text-[10.5px] font-bold uppercase tracking-wider text-purple-200 mb-1";

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-[150px_1fr_1fr_140px] gap-3 items-end mt-5">
        <label className="block">
          <span className={label}>Ville</span>
          <select
            value={city}
            onChange={(e) => {
              setCity(e.target.value);
              const l = LANDMARKS[e.target.value] ?? [];
              setPickup(l[0] ?? "");
              setDest(l[1] ?? "");
            }}
            className={selectCls}
          >
            {Object.keys(LANDMARKS).map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className={label}>Prise en charge</span>
          <input list="taxi-places" value={pickup} onChange={(e) => setPickup(e.target.value)} placeholder="Adresse, quartier, lieu connu" className={selectCls} />
        </label>
        <label className="block">
          <span className={label}>Destination</span>
          <input list="taxi-places" value={dest} onChange={(e) => setDest(e.target.value)} placeholder="Adresse, quartier, lieu connu" className={selectCls} />
        </label>
        <div>
          <span className={label}>Passagers</span>
          <Stepper value={pax} min={1} max={7} onChange={setPax} />
        </div>
        <datalist id="taxi-places">
          {places.map((p) => (
            <option key={p} value={p} />
          ))}
        </datalist>
      </div>
      <div className="flex items-center gap-3 flex-wrap mt-4 text-xs">
        <div className="flex p-0.5 bg-white/10 rounded-lg">
          {(
            [
              ["now", "Maintenant"],
              ["later", "Planifier"],
            ] as const
          ).map(([k, l]) => (
            <button key={k} type="button" onClick={() => setWhen(k)} className={classNames("px-3 py-1.5 rounded-md font-semibold", when === k ? "bg-white text-[#4c1d95]" : "text-purple-100")}>
              {l}
            </button>
          ))}
        </div>
        {when === "later" && <input type="datetime-local" value={at} min={`${TODAY}T00:00`} onChange={(e) => setAt(e.target.value)} className="h-9 px-2 rounded-lg bg-white text-[#1f1b2e] text-xs font-semibold" />}
        {valid && (
          <span className="text-purple-100">
            Distance estimée <strong className="text-white">{km} km</strong>
            {night && " · tarif de nuit (+30 %)"}
          </span>
        )}
      </div>

      <div className="mt-5 -mx-5 md:-mx-6 -mb-5 md:-mb-6 p-4 md:p-5 bg-[#faf7ff] rounded-b-2xl text-[#1f1b2e]">
        {!valid ? (
          <div className="p-6 bg-white border border-dashed border-[#ddd6fe] rounded-xl text-center text-sm">Indiquez un lieu de prise en charge et une destination différente.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-2.5">
            {TAXI_CLASSES.map((c) => {
              const price = Math.round(((c.base + c.perKm * km) * (night ? 1.3 : 1)) / 50) * 50;
              const minutes = Math.round((km / c.speed) * 60) + 5;
              const fits = pax <= c.seats;
              const eta = 3 + (distanceKm(city, c.k) % 9);
              return (
                <div key={c.k} className={classNames("p-4 bg-white border rounded-xl flex items-center gap-3", fits ? "border-[#ece7f8] hover:border-purple-200" : "border-[#f1f0f5] opacity-60")}>
                  <div className="w-11 h-11 rounded-xl bg-[#ede9fe] text-[#5b21b6] flex items-center justify-center shrink-0">
                    <MapPinIcon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm">{c.label}</div>
                    <div className="text-[11px] text-gray-500">
                      {c.note} · {c.seats} place{c.seats > 1 ? "s" : ""}
                    </div>
                    <div className="text-[11px] text-gray-600 mt-1 flex items-center gap-1">
                      <ClockIcon className="w-3.5 h-3.5" />
                      {when === "now" ? `Chauffeur à ${eta} min · ` : ""}trajet ~{minutes} min
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-base font-bold">{money(price)}</div>
                    <button
                      type="button"
                      disabled={!fits}
                      onClick={() => setBooking(c)}
                      className="mt-1 h-8 px-3.5 rounded-lg bg-[#6d28d9] hover:bg-purple-800 disabled:bg-gray-200 disabled:text-gray-400 text-white text-xs font-bold"
                      title={fits ? undefined : `${c.seats} place(s) maximum`}
                    >
                      {fits ? "Commander" : "Trop de passagers"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {booking && (
        <PreBookingModal
          title={`${booking.label} · ${city}`}
          lines={[`${pickup} → ${dest}`, when === "now" ? "Départ immédiat" : `Prise en charge le ${fDateCap(at.slice(0, 10))} à ${at.slice(11, 16)}`, `${pax} passager${pax > 1 ? "s" : ""} · ${km} km estimés`]}
          total={Math.round(((booking.base + booking.perKm * km) * (night ? 1.3 : 1)) / 50) * 50}
          prefix="TAX"
          onClose={() => setBooking(null)}
          onDone={(code) => flash(`Course ${code} transmise aux chauffeurs disponibles`)}
        />
      )}
    </>
  );
}

/* --------------------------------- Shared --------------------------------- */

function Stepper({ value, min, max, onChange }: { value: number; min: number; max: number; onChange: (n: number) => void }) {
  return (
    <div className="h-11 flex items-center justify-between rounded-xl bg-white text-[#1f1b2e] px-1.5">
      <button type="button" disabled={value <= min} onClick={() => onChange(value - 1)} className="w-8 h-8 rounded-lg hover:bg-gray-100 disabled:text-gray-300 flex items-center justify-center" aria-label="Moins">
        <MinusIcon className="w-4 h-4" />
      </button>
      <span className="text-sm font-bold">{value}</span>
      <button type="button" disabled={value >= max} onClick={() => onChange(value + 1)} className="w-8 h-8 rounded-lg hover:bg-gray-100 disabled:text-gray-300 flex items-center justify-center" aria-label="Plus">
        <PlusIcon className="w-4 h-4" />
      </button>
    </div>
  );
}

function PreBookingModal({ title, lines, total, prefix, onClose, onDone }: { title: string; lines: string[]; total: number; prefix: string; onClose: () => void; onDone: (code: string) => void }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState<string | null>(null);
  const valid = name.trim() && phone.replace(/\D/g, "").length >= 8;
  const input = "w-full h-10 px-3 border border-[#e2e8f0] rounded-lg text-sm";
  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center p-4 bg-[rgba(31,27,46,0.5)] text-[#1f1b2e]" onMouseDown={onClose}>
      <div onMouseDown={(e) => e.stopPropagation()} className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className="px-5 py-4 border-b border-[#ece7f8] flex items-start">
          <div className="flex-1">
            <div className="text-base font-bold">{code ? "Pré-réservation envoyée" : "Pré-réservation"}</div>
            <div className="text-xs text-gray-500 mt-0.5">{title}</div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 text-gray-500">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div className="p-3 bg-[#f8f7fc] rounded-xl space-y-1 text-xs text-gray-700">
            {lines.map((l) => (
              <div key={l}>{l}</div>
            ))}
            <div className="pt-1.5 mt-1.5 border-t border-[#ece7f8] flex justify-between font-bold text-sm text-[#1f1b2e]">
              <span>Total estimé</span>
              <span>{money(total)}</span>
            </div>
          </div>
          {code ? (
            <div className="text-center py-2">
              <CheckCircleIcon className="w-12 h-12 text-emerald-500 mx-auto" />
              <div className="font-mono text-xl font-bold text-[#6d28d9] mt-2">{code}</div>
              <div className="text-xs text-gray-500 mt-1">Le client reçoit la confirmation et le lien de paiement par SMS / WhatsApp.</div>
            </div>
          ) : (
            <>
              <label className="block">
                <span className="block text-xs font-semibold text-gray-600 mb-1">Nom du client</span>
                <input value={name} onChange={(e) => setName(e.target.value)} className={input} />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold text-gray-600 mb-1">Téléphone / WhatsApp</span>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+237 6XX XX XX XX" className={input} />
              </label>
            </>
          )}
        </div>
        <div className="px-5 py-3 bg-gray-50 border-t border-[#ece7f8] flex justify-end gap-2">
          {code ? (
            <button type="button" onClick={onClose} className="h-10 px-5 bg-[#6d28d9] rounded-lg text-sm font-bold text-white">
              Terminer
            </button>
          ) : (
            <>
              <button type="button" onClick={onClose} className="h-10 px-4 border border-gray-300 bg-white rounded-lg text-sm font-semibold">
                Annuler
              </button>
              <button
                type="button"
                disabled={!valid}
                onClick={() => {
                  const c = `${prefix}-${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
                  setCode(c);
                  onDone(c);
                }}
                className="h-10 px-5 bg-[#6d28d9] disabled:bg-purple-200 rounded-lg text-sm font-bold text-white"
              >
                Envoyer la pré-réservation
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

